import type { TextInputProps } from 'react-native';
import React from 'react';
import { View, TextInput, StyleSheet, Platform } from 'react-native';
import { Search } from '../icons';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { fontSize, lineHeight } from '../../theme/typography';

export type FormSearchFieldProps = {
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  accessibilityLabel?: string;
  testID?: string;
} & Pick<TextInputProps, 'autoCorrect' | 'autoCapitalize' | 'returnKeyType' | 'editable'>;

const SEARCH_ICON_SIZE = 16;

/**
 * Outlined search row aligned with Visual Template Input / Select in-menu filter (`spacing.formControlHeight` / LG band),
 * 16px icon, 16/24 value typography, 12px horizontal inset, 4px radius.
 */
export function FormSearchField({
  value,
  onChangeText,
  placeholder = 'Type to search',
  accessibilityLabel,
  testID,
  autoCorrect = false,
  autoCapitalize = 'none',
  returnKeyType = 'search',
  editable = true,
}: FormSearchFieldProps) {
  const textInputA11y =
    accessibilityLabel == null || accessibilityLabel.length === 0 ? placeholder : accessibilityLabel;
  return (
    <View style={styles.searchShell} importantForAccessibility="no">
      <View style={styles.iconWrap} pointerEvents="none" importantForAccessibility="no-hide-descendants" accessibilityElementsHidden>
        <Search size={SEARCH_ICON_SIZE} color={colors.textSecondary} />
      </View>
      <TextInput
        testID={testID}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={colors.inputPlaceholder}
        style={styles.input}
        accessible
        accessibilityLabel={textInputA11y}
        autoCorrect={autoCorrect}
        autoCapitalize={autoCapitalize}
        returnKeyType={returnKeyType}
        editable={editable}
        clearButtonMode={Platform.OS === 'ios' ? 'while-editing' : 'never'}
        selectionColor={colors.primary}
        {...Platform.select({
          web: {
            onMouseDown: (e: { stopPropagation: () => void }) => e.stopPropagation(),
            autoComplete: 'off',
            type: 'search' as const,
          },
        })}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  searchShell: {
    flex: 1,
    minWidth: 0,
    minHeight: spacing.formControlHeight,
    height: spacing.formControlHeight,
    maxHeight: spacing.formControlHeight,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.backgroundForm,
    borderWidth: 1,
    borderColor: colors.inputBorderNeutral,
    borderRadius: spacing.formControlBorderRadius,
    paddingLeft: spacing.formControlPaddingHorizontal,
    paddingRight: spacing.sm,
    ...Platform.select({
      web: {
        boxSizing: 'border-box' as const,
        ':focus-within': {
          borderColor: colors.inputBorderFocus,
          boxShadow: colors.formControlFocusRing,
        },
        ':hover': {
          borderColor: colors.inputBorderInteractive,
        },
      },
    }),
  },
  iconWrap: {
    marginRight: spacing.formControlIconGap,
    justifyContent: 'center',
    alignItems: 'center',
  },
  input: {
    flex: 1,
    minWidth: 0,
    minHeight: spacing.formControlHeight,
    padding: 0,
    paddingTop: 0,
    paddingBottom: 0,
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    color: colors.textPrimary,
    margin: 0,
    ...Platform.select({
      web: {
        outlineStyle: 'none' as const,
        outlineWidth: 0,
        '::placeholder': {
          color: colors.inputPlaceholder,
          opacity: 1,
        },
      },
    }),
  },
});
